/**
 * Pure utility functions used across the application.
 * No React or Supabase imports – these are plain TypeScript helpers.
 */

// ---------------------------------------------------------------------------
// Vimeo
// ---------------------------------------------------------------------------

/**
 * Extract the Vimeo video ID from a URL like `https://vimeo.com/123456789`
 * or `https://player.vimeo.com/video/123456789`.
 */
export function extractVimeoId(url: string): string {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return "";

  try {
    const parsed = new URL(trimmedUrl);
    const segments = parsed.pathname.split("/").filter(Boolean);
    return segments.at(-1) ?? "";
  } catch {
    const normalized = trimmedUrl
      .split(/[?#]/, 1)[0]
      ?.replace(/\/+$/, "");
    return normalized?.split("/").filter(Boolean).at(-1) ?? "";
  }
}

/** Build an embeddable Vimeo player URL. */
export function buildVimeoEmbedUrl(
  videoUrl: string,
  options?: {
    autoplay?: boolean;
    loop?: boolean;
    muted?: boolean;
    background?: boolean;
  },
): string {
  const id = extractVimeoId(videoUrl);
  const params = new URLSearchParams({
    title: "0",
    byline: "0",
    portrait: "0",
    badge: "0",
    autopause: "0",
    player_id: "0",
    app_id: "58479",
    transparent: "0",
  });

  if (options?.autoplay) params.set("autoplay", "1");
  if (options?.loop) params.set("loop", "1");
  if (options?.muted) params.set("muted", "1");
  if (options?.background) params.set("background", "1");

  return `https://player.vimeo.com/video/${id}?${params.toString()}`;
}

/** Build a background-video preview URL (autoplay, loop, muted). */
export function buildVimeoPreviewUrl(videoUrl: string): string {
  return buildVimeoEmbedUrl(videoUrl, {
    autoplay: true,
    loop: true,
    muted: true,
    background: true,
  });
}

// ---------------------------------------------------------------------------
// Time / Duration
// ---------------------------------------------------------------------------

/** Format seconds as `M:SS` (e.g. `3:05`). */
export function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Format minutes as `HH:MM` (e.g. `01:30`). */
export function formatDuration(totalMinutes: number): string {
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
  const minutes = String(totalMinutes % 60).padStart(2, "0");
  return `${hours}:${minutes}`;
}

// ---------------------------------------------------------------------------
// Expiry
// ---------------------------------------------------------------------------

const DAY_IN_MS = 24 * 60 * 60 * 1000;

function getExpiryTimestamp(expiresAt: string): number | null {
  const timestamp = new Date(expiresAt).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

/** Calculate days remaining from an ISO expiry timestamp. Returns `-1` once expired. */
export function daysRemaining(expiresAt: string): number {
  const expiryTimestamp = getExpiryTimestamp(expiresAt);
  if (expiryTimestamp == null) return -1;

  const remainingMs = expiryTimestamp - Date.now();
  if (remainingMs <= 0) return -1;

  const remainingDays = Math.ceil(remainingMs / DAY_IN_MS);
  return Object.is(remainingDays, -0) ? 0 : remainingDays;
}

/** Check whether a permission has expired. */
export function isExpired(expiresAt: string): boolean {
  const expiryTimestamp = getExpiryTimestamp(expiresAt);
  return expiryTimestamp == null || expiryTimestamp <= Date.now();
}

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

/** Calculate progress percentage (0–100). */
export function progressPercent(
  completed: number,
  total: number,
): number {
  if (total <= 0) return 0;
  return Math.round((completed / total) * 100);
}

// ---------------------------------------------------------------------------
// Cart helpers
// ---------------------------------------------------------------------------

const CART_STORAGE_KEY = "bollyrhythm_cart";
const SAVED_STORAGE_KEY = "bollyrhythm_saved";
const SAVED_BUNDLE_STORAGE_KEY = "bollyrhythm_saved_bundles";

/** Read cart items from localStorage, normalizing legacy items without a `type` field. */
export function readCartFromStorage(): Array<import("./schema").CartItem> {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown[] = JSON.parse(raw);
    return parsed.map((item: any) => {
      if (item.type === "add_on" || item.type === "course") return item;
      if (item.type === "bundle") {
        return {
          ...item,
          course_ids: Array.isArray(item.course_ids)
            ? item.course_ids.filter((value: unknown): value is string => typeof value === "string")
            : [],
          course_count: Number(item.course_count ?? 0),
        };
      }
      // Legacy items have no `type` — treat as course
      return { type: "course" as const, course_id: item.course_id, name: item.name, price: item.price };
    });
  } catch {
    return [];
  }
}

/** Write cart items to localStorage. */
export function writeCartToStorage(
  items: Array<import("./schema").CartItem>,
): void {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
}

/** Read saved/bookmarked course IDs from localStorage. */
export function readSavedFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(SAVED_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Write saved course IDs to localStorage. */
export function writeSavedToStorage(ids: string[]): void {
  localStorage.setItem(SAVED_STORAGE_KEY, JSON.stringify(ids));
}

/** Read saved/bookmarked bundle IDs from localStorage. */
export function readSavedBundlesFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(SAVED_BUNDLE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Write saved bundle IDs to localStorage. */
export function writeSavedBundlesToStorage(ids: string[]): void {
  localStorage.setItem(SAVED_BUNDLE_STORAGE_KEY, JSON.stringify(ids));
}

// ---------------------------------------------------------------------------
// Share URLs
// ---------------------------------------------------------------------------

const BASE_URL =
  typeof window !== "undefined"
    ? window.location.origin
    : "https://bollyrhythm.com";

/** Build a shareable tutorial URL. */
export function buildShareTutorialUrl(shareToken: string): string {
  return `${BASE_URL}/app/shared-tutorial?shared_tutorial=${shareToken}`;
}

/** Build a shareable playlist URL. */
export function buildSharePlaylistUrl(invitationToken: string): string {
  return `${BASE_URL}/app/shared-playlist?shared_playlist=${invitationToken}`;
}

/** Derive a filename for a music mix download from its URL. */
export function getMusicMixDownloadFilename(url: string, fallbackName = "music-mix.mp3"): string {
  try {
    const basename = new URL(url).pathname.split("/").filter(Boolean).at(-1);
    if (basename) return basename;
  } catch {
    // Fall through to the default filename below.
  }

  return fallbackName;
}

/**
 * Build a URL that triggers a direct file download instead of inline playback.
 * Supabase Storage honors the `download` query param with Content-Disposition: attachment.
 */
export function buildForcedDownloadUrl(url: string, filename?: string): string {
  try {
    const parsed = new URL(url);
    const isSupabaseStorageUrl = parsed.pathname.includes("/storage/v1/object/");

    if (!isSupabaseStorageUrl) {
      return url;
    }

    parsed.searchParams.set("download", filename ?? getMusicMixDownloadFilename(url));
    return parsed.toString();
  } catch {
    return url;
  }
}

/** Copy text to clipboard with Safari-friendly fallbacks. */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to legacy copy methods below.
    }
  }

  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    try {
      if (document.execCommand("copy")) {
        return true;
      }
    } catch {
      // Fall through to manual-copy prompt below.
    } finally {
      document.body.removeChild(textarea);
    }
  }

  if (typeof window !== "undefined" && typeof window.prompt === "function") {
    window.prompt("Copy this link:", text);
  }

  return false;
}
